import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Nav, Container, Row, Col, Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import {
  selectSelectedProjectId,
  selectActiveLabelingSubView,
  setActiveLabelingSubView,
} from '../../../store/slices/projectSlice'
import { selectDatasetSources, setDatasetSources } from '../../../store/slices/datasetSlice'
import { selectEditingContent } from '../../../store/slices/annotationSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { Database, Tag } from 'lucide-react'
import { PageWithTabs } from '../../common'
import DatasetLabelingView from '../DatasetLabelingView/DatasetLabelingView'
import DatasetManageTags from '../DatasetManageTags/DatasetManageTags'
import SelectDatasetModal from '../../modals/SelectDatasetModal'
import PillsDataset from '../../PillsDataset'
import styled from 'styled-components'

const LabelingContainer = styled.div`
  background: rgba(26, 26, 26, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  min-height: 100vh;
  padding: 1.5rem;
  backdrop-filter: blur(5px);
`

const NavContainer = styled.div`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0 2rem;
  margin-bottom: 1.5rem;

  .nav-tabs {
    border-bottom: none;
    margin-bottom: 0;

    .nav-link {
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.7);
      padding: 1rem 1.5rem;
      font-weight: 500;
      font-size: 1rem;
      transition: all 0.2s ease;

      &:hover {
        color: #ffffff;
        background: rgba(255, 255, 255, 0.05);
        border-color: transparent;
      }

      &.active {
        color: #800085;
        background: transparent;
        border-color: transparent;
        border-bottom: 3px solid #800085;
        font-weight: 600;
      }
    }
  }
`

const TabContent = styled.div`
  padding: 0;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-height: calc(100vh - 200px);
`

const DatasetSelectorContainer = styled.div`
  background: rgba(42, 42, 42, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem 2rem;
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  gap: 1rem;
`

const DatasetSelector = styled.div`
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;

  &:hover {
    border-color: var(--bs-primary);
    background: rgba(255, 255, 255, 0.1);
  }

  .dataset-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pills-container {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 0.5rem;
  }
`

const LabelingView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const activeLabelingSubView = useAppSelector(selectActiveLabelingSubView)
  const editingContent = useAppSelector(selectEditingContent)
  const [showDatasetModal, setShowDatasetModal] = useState(false)

  // Local state for dataset selection - defaults to ALL datasets
  const [localSelectedDatasets, setLocalSelectedDatasets] = useState<string[]>(['All'])

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  // Redirect to projects if no project selected
  useEffect(() => {
    if (!selectedProjectId && !projectId) {
      navigate('/projects')
    }
  }, [selectedProjectId, projectId, navigate])

  const handleTabSelect = (tab: string | null) => {
    if (tab) {
      dispatch(setActiveLabelingSubView(tab as any))
    }
  }

  const renderDatasetPills = () => {
    if (!localSelectedDatasets?.length) {
      return <PillsDataset tiny datasetNames={['All']} disabled={true} />
    }

    const visibleDatasets = localSelectedDatasets.slice(0, 3)
    const remainingCount = localSelectedDatasets.length - 3

    return (
      <div className="pills-container">
        {visibleDatasets.map((dataset: string, index: number) => (
          <PillsDataset key={index} tiny datasetNames={[dataset]} disabled={true} />
        ))}
        {remainingCount > 0 && <span className="text-muted">+{remainingCount} more</span>}
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeLabelingSubView) {
      case 'Labeling':
        return <DatasetLabelingView datasetSources={localSelectedDatasets} />
      case 'Tags':
        return <DatasetManageTags />
      default:
        return <DatasetLabelingView datasetSources={localSelectedDatasets} />
    }
  }

  const tabItems = [
    {
      key: 'Labeling',
      label: 'Labeling',
      icon: <Database size={20} />,
    },
    {
      key: 'Tags',
      label: 'Tags',
      icon: <Tag size={20} />,
    },
  ]

  return (
    <PageWithTabs
      tabs={tabItems}
      activeTab={activeLabelingSubView}
      onTabSelect={handleTabSelect}
      tabContent={() => (
        <>
          {/* Only show dataset selector when NOT in annotation view (drawing boxes) */}
          {!editingContent && (
            <DatasetSelectorContainer>
              <OverlayTrigger placement="top" overlay={<Tooltip>Click to select datasets</Tooltip>}>
                <DatasetSelector onClick={() => setShowDatasetModal(true)}>
                  <div className="dataset-label">
                    <Database size={16} />
                    <span>Active Dataset(s)</span>
                  </div>
                  {renderDatasetPills()}
                </DatasetSelector>
              </OverlayTrigger>
            </DatasetSelectorContainer>
          )}
          {renderTabContent()}
        </>
      )}
    >
      <SelectDatasetModal
        show={showDatasetModal}
        onHide={() => setShowDatasetModal(false)}
        handleAcceptSelections={(sources) => {
          setLocalSelectedDatasets(sources)
          setShowDatasetModal(false)
        }}
        selectedSources={localSelectedDatasets}
        canCreate={true}
      />
    </PageWithTabs>
  )
}

export default LabelingView
